import { useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { LGoFormField } from "@/ui/branded";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { RadioGroup, RadioGroupItem } from "@/ui/primitives/radio-group";
import { Switch } from "@/ui/primitives/switch";
import { SaleCloseField } from "./SaleCloseField";
import { saleCloseCopy } from "@/features/activities/sale-close-copy";
import ActivityImageField from "./ActivityImageField";
import { CustomFieldsEditor } from "@/features/custom-fields/components/CustomFieldsEditor";
import { customFieldsCopy } from "@/features/custom-fields/custom-fields-copy";
import type { CustomFieldRow } from "@/features/custom-fields/form-schema";
import type { ActivityFormData } from "@/features/activities/form-schema";
import type { ActivitySubcategory } from "@/domain/activity/types";
import type { AuthorableBookingMode } from "@/domain/activity/rules";

const bookingModeChoices: {
  value: AuthorableBookingMode;
  label: string;
  description: string;
}[] = [
  {
    value: "session",
    label: "Pevný termín",
    description: "Zákazník si rezervuje jeden konkrétní čas začátku, který určíte.",
  },
  {
    value: "entry",
    label: "Vstup v otevírací době",
    description: "Zákazník koupí vstup platný po celý termín a přijde, kdy mu to vyhovuje.",
  },
];

interface ActivityFormFieldsProps {
  subcategories: ActivitySubcategory[];
  businessId: number | null;
  isSubmitting: boolean;
  /** Image-upload session bookkeeping: surface URLs uploaded inside this
   *  dialog instance so the shell can clean them up on cancel. */
  onPendingCleanup: (urls: string[]) => void;
}

export function ActivityFormFields({
  subcategories,
  businessId,
  isSubmitting,
  onPendingCleanup,
}: ActivityFormFieldsProps) {
  const { control, formState, trigger } = useFormContext<ActivityFormData>();
  const sessionUploadsRef = useRef<string[]>([]);

  // Arrival order is the server's order. Sorting again here would put this
  // picker in a different order from every other client.
  const grouped = useMemo(() => {
    const groups = new Map<number, { categoryName: string; items: ActivitySubcategory[] }>();
    for (const sub of subcategories) {
      const group = groups.get(sub.categoryId);
      if (group) {
        group.items.push(sub);
        continue;
      }
      groups.set(sub.categoryId, {
        categoryName: sub.category?.displayName ?? "Ostatní",
        items: [sub],
      });
    }
    return Array.from(groups.entries()).map(([id, value]) => ({ id, ...value }));
  }, [subcategories]);

  return (
    <div className="space-y-4">
      <LGoFormField
        name="activity_name"
        label="Název šablony slev"
        required
        placeholder="např. Lekce jógy"
      />

      <LGoFormField name="image_url" label="Obrázek šablony slev" required>
        {({ value, onChange }) =>
          businessId != null ? (
            <ActivityImageField
              value={(value as string) || null}
              onChange={(url) => onChange(url ?? "")}
              businessId={businessId}
              disabled={isSubmitting}
              onPendingCleanup={(urls) => {
                sessionUploadsRef.current = urls;
                onPendingCleanup(urls);
              }}
            />
          ) : null
        }
      </LGoFormField>

      <div className="grid grid-cols-2 gap-3">
        <LGoFormField name="activity_subcategory_id" label="Kategorie" required>
          {({ value, onChange }) => (
            <Controller
              name="activity_subcategory_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value != null ? String(field.value) : ""}
                  onValueChange={(v) => field.onChange(v ? Number(v) : undefined)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger
                    className={
                      formState.errors.activity_subcategory_id ? "border-destructive" : ""
                    }
                  >
                    <SelectValue placeholder="Vyberte kategorii" />
                  </SelectTrigger>
                  <SelectContent>
                    {grouped.map((group) => (
                      <SelectGroup key={group.id}>
                        <SelectLabel>{group.categoryName}</SelectLabel>
                        {group.items.map((sub) => (
                          <SelectItem key={sub.id} value={String(sub.id)}>
                            {sub.displayName}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          )}
        </LGoFormField>

        <LGoFormField
          name="price_original"
          label="Původní cena (Kč)"
          type="number"
          required
          placeholder="např. 500"
          hint="Standardní cena služby bez slevy"
        />
      </div>

      <LGoFormField name="booking_mode" label="Způsob rezervace" required>
        {({ value, onChange }) => (
          <RadioGroup
            value={value as AuthorableBookingMode}
            onValueChange={onChange}
            disabled={isSubmitting}
            className="grid grid-cols-2 gap-3"
          >
            {bookingModeChoices.map((choice) => (
              <div
                key={choice.value}
                className="flex items-start gap-2 rounded-md border border-border p-3"
              >
                <RadioGroupItem
                  value={choice.value}
                  id={`booking_mode-${choice.value}`}
                  className="mt-1"
                />
                <label htmlFor={`booking_mode-${choice.value}`} className="cursor-pointer">
                  <span className="block text-sm font-medium text-foreground">{choice.label}</span>
                  <span className="block text-xs text-muted-foreground">{choice.description}</span>
                </label>
              </div>
            ))}
          </RadioGroup>
        )}
      </LGoFormField>

      <div className="space-y-4 rounded-md border border-border p-4">
        <div>
          <h3 className="text-sm font-medium text-foreground">
            Výchozí hodnoty slevových termínů
          </h3>
          <p className="text-xs text-muted-foreground">
            Předvyplní každý slevový termín, který z této šablony vypíšete od teď. U termínu je
            pak můžete přepsat a už vypsané termíny si ponechají to, s čím byly vypsané.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <LGoFormField
            name="default_price_discounted"
            label="Zlevněná cena (Kč)"
            type="number"
            placeholder="např. 350"
            hint="Nevyplněno: cenu zadáte u každého termínu."
          />

          <LGoFormField
            name="default_capacity"
            label="Počet míst"
            type="number"
            placeholder="např. 8"
            hint="Nevyplněno: počet míst zadáte u každého termínu."
          />
        </div>

        <LGoFormField
          name="default_duration_minutes"
          label={saleCloseCopy.durationLabel}
          hint={saleCloseCopy.durationHint}
        >
          {({ value, onChange, onBlur, name }) => (
            <Input
              type="number"
              min={1}
              max={1440}
              inputMode="numeric"
              aria-label={saleCloseCopy.durationLabel}
              placeholder={saleCloseCopy.durationPlaceholder}
              value={typeof value === "number" && !Number.isNaN(value) ? value : ""}
              onChange={(event) => {
                onChange(event.target.valueAsNumber);
                void trigger("sale_end_offset_minutes");
              }}
              onBlur={onBlur}
              name={name}
              disabled={isSubmitting}
            />
          )}
        </LGoFormField>

        <LGoFormField
          name="sale_lead_time_hours"
          label="Prodej otevřít (hodin před začátkem)"
          type="number"
          placeholder="např. 24"
          hint="Nevyplněno: termín se začne prodávat podle výchozího nastavení LastGO."
        />

        <SaleCloseField disabled={isSubmitting} />

        <LGoFormField
          name="requires_confirmation"
          label="Rezervace potvrzuji ručně"
          description="Zapnuto: každou rezervaci musíte potvrdit, nepotvrzená nebo odmítnutá rezervace zákazníkovi uvolní celou částku; vypnuto: rezervace se potvrdí sama. Peníze zákazníkovi držíme v obou případech a strhneme je až noc po termínu."
        >
          {({ value, onChange }) => (
            <Switch
              checked={value === true}
              onCheckedChange={(checked) => {
                onChange(checked);
                void trigger("sale_end_anchor");
              }}
              disabled={isSubmitting}
              aria-label="Rezervace potvrzuji ručně"
            />
          )}
        </LGoFormField>
      </div>

      <LGoFormField name="description" label="Popis šablony slev" hint="Max 2000 znaků">
        {({ value, onChange, onBlur, name }) => (
          <Textarea
            placeholder="např. Lekce vhodná pro začátečníky."
            rows={5}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            name={name}
            disabled={isSubmitting}
          />
        )}
      </LGoFormField>

      <LGoFormField
        name="custom_fields"
        label={customFieldsCopy.sectionLabel}
        description={customFieldsCopy.sectionDescription}
      >
        {({ value, onChange }) => (
          <CustomFieldsEditor
            rows={value as CustomFieldRow[]}
            onChange={onChange}
            disabled={isSubmitting}
          />
        )}
      </LGoFormField>
    </div>
  );
}
